import type { BagType, Contribution, Insurance } from '@optionway/models';

// item_category is the product family; the travel facets are named item parameters, never item_categoryN — docs/tag-plan.md § items[].
export interface TagPlanItem {
  item_id?: string;
  item_name?: string;
  item_brand?: string;
  item_variant?: string;
  item_category?: string;
  trip_type?: string;
  stops_bucket?: string;
  booking_window?: string;
  contribution_ratio?: number;
  price?: number;
  quantity?: number;
  index?: number;
}

// The one nested object GA4 imposes: monetary and item fields live here, everything else is top level.
export interface TagPlanEcommerce {
  currency?: string;
  value?: number;
  tax?: number;
  transaction_id?: string;
  item_list_id?: string;
  items: TagPlanItem[];
}

// The committed search as event parameters, built by useTagPlanStore().searchContext and carried by
// every event from `search` to `purchase_failed`.
export interface TagPlanSearchContext {
  trip_type?: string;
  cabin_class?: string;
  origin?: string;
  destination?: string;
  route?: string;
  departure_date?: string;
  return_date?: string;
  booking_window?: string;
  pax_adults?: number;
  pax_children?: number;
  pax_infants?: number;
  pax_total?: number;
  legs_count?: number;
}

const BAGGAGE = 'baggage';
const INSURANCE = 'insurance';
const CONTRIBUTION = 'contribution';

// item_variant is deliberately absent — it only ever repeated item_id, and the model exposes nothing
// better on a SelectedBag (uid and providerId are on the never-send list).
// The cart states a total and a piece count; GA4 wants a unit price and a quantity. The division
// belongs here, at the boundary — never in the store, which would then hold a price nobody charges.
export const bagTagPlanItem = (type: BagType, count: number, value: number): TagPlanItem => ({
  item_id: `bag_${type.toLowerCase()}`,
  item_name: `${type} bag`,
  item_category: BAGGAGE,
  price: roundToCents(value / count),
  quantity: count,
});

export const insuranceTagPlanItem = (insurance: Insurance): TagPlanItem => ({
  item_id: `insurance_${insurance.type}`,
  item_name: `Insurance ${insurance.type}`,
  item_category: INSURANCE,
  price: insurance.price.averagePerPax,
  quantity: insurance.price.paxCount,
});

// projectId stays out — a supplier identifier on the never-send list.
export const contributionTagPlanItem = (contribution: Contribution): TagPlanItem => ({
  item_id: 'contribution_carbon',
  item_name: 'Carbon contribution',
  item_category: CONTRIBUTION,
  contribution_ratio: contribution.ratio,
  price: contribution.price,
  quantity: 1,
});
